import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from '../theme';

/**
 * Zentrale Verwaltung des Farbmodus mit drei Zuständen: 'system' | 'light' | 'dark'.
 *
 * - Persistiert die Auswahl in localStorage unter 'ly-ui-color-mode'
 *   (gleiches Namensschema wie 'ly-ui-language').
 * - Bei 'system' folgt die App der OS-Einstellung inkl. Live-Wechsel.
 * - Stellt neben dem React-Context auch `document.documentElement.dataset.theme`
 *   bereit, damit globales CSS (Ticket 4) und Vendor-CSS (Ticket 7) am
 *   MUI-Theme vorbei umschalten können.
 * - Wrappt die App direkt im MUI-ThemeProvider + CssBaseline, damit beide
 *   Einstiegspunkte (index.js, withRoot.js) denselben Modus teilen.
 */

export const COLOR_MODE_STORAGE_KEY = 'ly-ui-color-mode';
const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

const ColorModeContext = createContext({
  mode: 'system',
  resolvedMode: 'light',
  setMode: () => {},
});

export function useColorMode() {
  return useContext(ColorModeContext);
}

function getSystemMode() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia(DARK_MEDIA_QUERY).matches ? 'dark' : 'light';
}

function readStoredMode() {
  try {
    const stored = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch (e) {
    /* localStorage nicht verfügbar (z.B. Private Mode) → Default */
  }
  return 'system';
}

export default function ColorModeProvider({ children }) {
  const [mode, setModeState] = useState(readStoredMode);
  const [systemMode, setSystemMode] = useState(getSystemMode);

  // OS-Einstellung live verfolgen (relevant im 'system'-Modus).
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mql = window.matchMedia(DARK_MEDIA_QUERY);
    const handler = (event) => setSystemMode(event.matches ? 'dark' : 'light');
    // addEventListener ist der moderne Weg; addListener als Safari-Fallback.
    if (mql.addEventListener) {
      mql.addEventListener('change', handler);
    } else {
      mql.addListener(handler);
    }
    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener('change', handler);
      } else {
        mql.removeListener(handler);
      }
    };
  }, []);

  const resolvedMode = mode === 'system' ? systemMode : mode;

  const setMode = useCallback((next) => {
    setModeState(next);
    try {
      window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, next);
    } catch (e) {
      /* Persistenz optional */
    }
  }, []);

  // Aufgelösten Modus auf <html data-theme> spiegeln (globales + Vendor-CSS).
  useEffect(() => {
    document.documentElement.dataset.theme = resolvedMode;
  }, [resolvedMode]);

  const theme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode]);

  const contextValue = useMemo(
    () => ({ mode, resolvedMode, setMode }),
    [mode, resolvedMode, setMode]
  );

  return (
    <ColorModeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
